import React, { useMemo, useRef } from 'react'
import styled from 'styled-components'

import { Swiper, SwiperSlide } from 'swiper/react'
import RightArrow from 'src/assets/img/icons/RightCarouselArrow.svg'
import LeftArrow from 'src/assets/img/icons/LeftCarouselArrow.svg'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { graphql, useStaticQuery } from 'gatsby'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'

import 'src/styles/swiper.css'
import { LargeP, MonoP, P } from 'src/components/shared/text'
import { colors, fonts } from 'src/styles/variables'
import { convertLineItemsToSlugs } from 'src/utils/checkout'
import { useRecoilState } from 'recoil'
import { trackAddToCartEvent } from 'src/utils/event-tracking'
import { shopifyCartState } from 'src/store/cartAtom'
import { addVariantToCart } from 'src/actions/cart'
import useCart from 'src/hooks/useCart'
import { ProductDetailState } from 'src/actions/products'
import { LineItem } from 'src/types/cart'

const ArrowSVG = styled.svg`
  margin-left: 2px;
  fill: inherit;
  margin-bottom: -6px;
  height: 16px;
  width: 16px;
`

const ArrowSVGPath = styled.path`
  fill: white;
`
const ArrowIcon = () => (
  <ArrowSVG viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ArrowSVGPath d="M6.8 0L13.5 6.1L6.8 12.2L5.8 11L10.2 7H0V5.2H10.2L5.7 1.2L6.8 0Z" />
  </ArrowSVG>
)

const Root = styled.div`
  padding-bottom: 20px;
  cursor: pointer;
`

const RecommendationContainer = styled.div`
  display: flex;
  gap: 5px;
  width: 100%;
`

const SwiperContainer = styled.div`
  width: calc(100% - 70px);
`

const ArrowImg = styled.img`
  width: 30px;
  cursor: pointer;
`

const RecommendationSlide = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  grid-column-gap: 15px;
  padding: 12px;
`

const ProductImage = styled.img`
  align-self: center;
`

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ProductName = styled(P)`
  font-family: ${fonts.larsseit.regular};
  color: ${colors.lightText};
  line-height: 1.3 !important;
  padding-right: 9px;
  display: inline-block;
  width: fit-content;
  white-space: pre-line;
`

const ProductPrice = styled.div`
  display: flex;
  gap: 6px;
`

const Price = styled(LargeP)`
  font-weight: bold;
`

const StrikeThroughPrice = styled(LargeP)`
  text-decoration: line-through;
  color: #c0c0c0;
`

const StrikeThrough = styled.span`
  text-decoration: line-through;
`

const Header = styled(MonoP)`
  color: ${colors.lightText};
  padding-top: 3px;
  font-size: 16px;
  margin-bottom: 16px;
`

const CTAButton = styled.button`
  background-color: ${colors.orange};
  fill: ${colors.orange};
  color: ${colors.white};
  white-space: pre-wrap;
  border-radius: 20px;
  border: none;
  width: 120px;
  height: 26px;
  outline: none;
  cursor: pointer;
  margin: 4px 0;
  font-size: 11px;
  font-family: ${fonts.larsseit.bold};
  padding-bottom: 3px;
`

const query = graphql`
  query {
    pawHero: file(relativePath: { regex: "/paw-hero/main-img.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 250)
      }
    }
    allergyAndItch: file(relativePath: { regex: "/tins/allergy-and-itch.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 250)
      }
    }
    probiotics: file(relativePath: { regex: "/tins/probiotics.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 250)
      }
    }
    calmingAid: file(relativePath: { regex: "/tins/calming-aid.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 250)
      }
    }
    hipAndJoint: file(relativePath: { regex: "/tins/hip-and-joint.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 250)
      }
    }
    multivitamin: file(relativePath: { regex: "/tins/multivitamin.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 250)
      }
    }
    visionAndTear: file(relativePath: { regex: "/tins/vision-and-tear-stain.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 400)
      }
    }
  }
`

export default function RecommendationCarousel({
  lineItems,
  productMap
}: {
  lineItems: LineItem[]
  productMap: ProductDetailState
}) {
  const swiperRef = useRef(null)
  const images = useStaticQuery(query)
  const [cartState, setCartState] = useRecoilState(shopifyCartState)
  const { actualTotal } = useCart()

  const filteredRecommendations = useMemo(() => {
    if (!productMap || productMap.fetching || !productMap.bySlug) return []

    const recommendations = [
      {
        slug: 'probiotics',
        name: 'Probiotics | Today’s Offer',
        img: getImage(images.probiotics),
        originalPrice: 32
      },
      {
        slug: 'paw-hero',
        name: 'Paw Hero',
        img: getImage(images.pawHero),
        originalPrice: 25
      },
      {
        slug: 'allergy-and-itch',
        name: 'Allergy & Itch',
        img: getImage(images.allergyAndItch)
        // originalPrice: 42
      },
      {
        slug: 'calming-aid',
        name: 'Calming Aid',
        img: getImage(images.calmingAid)
        // originalPrice: 42
      },
      {
        slug: 'hip-and-joint',
        name: 'Hip & Joint',
        img: getImage(images.hipAndJoint)
        // originalPrice: 42
      },
      {
        slug: 'multivitamin',
        name: 'Multivitamin',
        img: getImage(images.multivitamin)
        // originalPrice: 42
      },
      {
        slug: 'vision-and-tear-stain',
        name: 'Vision & Tear',
        img: getImage(images.visionAndTear)
        // originalPrice: 42
      }
    ]
    const checkoutSlugs = convertLineItemsToSlugs(lineItems, productMap)
    return recommendations
      .filter((recommendation) => {
        return !checkoutSlugs.includes(recommendation.slug)
      })
      .filter((recommendation) => {
        return (
          recommendation.slug in productMap.bySlug && productMap.bySlug[recommendation.slug].single
        )
      })
  }, [lineItems, productMap])

  const goNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
    }
  }

  const goPrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
    }
  }

  const addToCart = async (slug) => {
    const price = productMap.bySlug[slug].single.pricing.oneTime.price
    const detail = {
      productMap,
      variantId: productMap.bySlug[slug].single.variantId.encoded,
      quantity: 1,
      frequency: null, // one-time
      cartState,
      setCartState,
      subscription: null,
      actualTotal,
      unitPrice: Number(price)
    }
    await addVariantToCart(detail)
    trackAddToCartEvent(
      productMap,
      slug,
      productMap.bySlug[slug].single.variantId.decoded,
      Number(price)
    )
  }

  if (filteredRecommendations.length === 0) return null
  if (!productMap || !productMap.bySlug) return null

  return (
    <Root>
      <Header>RECOMMENDED FOR YOU</Header>
      <RecommendationContainer>
        <ArrowImg src={LeftArrow} onClick={goPrev} />
        <SwiperContainer>
          <Swiper
            loop={true}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            className="mySwiper"
          >
            {filteredRecommendations.map((recommendation) => {
              const price = productMap.bySlug[recommendation.slug].single.pricing.oneTime.price
              return (
                <SwiperSlide key={recommendation.slug}>
                  <RecommendationSlide>
                    <GatsbyImage
                      image={recommendation.img}
                      imgStyle={{ objectFit: 'contain' }}
                      alt={recommendation.name}
                    />
                    <ProductInfo>
                      <ProductName>{recommendation.name}</ProductName>

                      <ProductPrice>
                        {recommendation.originalPrice && (
                          <StrikeThroughPrice>${recommendation.originalPrice}</StrikeThroughPrice>
                        )}
                        <Price>${price}</Price>
                      </ProductPrice>

                      <CTAButton onClick={() => addToCart(recommendation.slug)}>
                        Add to Cart <ArrowIcon />
                      </CTAButton>
                    </ProductInfo>
                  </RecommendationSlide>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </SwiperContainer>
        <ArrowImg src={RightArrow} onClick={goNext} />
      </RecommendationContainer>
    </Root>
  )
}
